import type { AutocompleteInputChangeReason } from "@mui/material";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useState } from "react";

export interface GenericAsyncAutocompleteProps<TOption, TValue> {
  // Query configuration
  queryKeyPrefix: string;
  queryFn: (query: string) => Promise<TOption[]>;
  minQueryLength?: number;
  debounceMs?: number;

  // Data mapping
  getOptionLabel: (option: TOption) => string;
  getOptionValue: (option: TOption) => TValue;
  isOptionEqualToValue?: (option: TOption, value: TValue) => boolean;

  // UI
  label: string;
  placeholder?: string;
  noOptionsText?: (query: string) => string;
  disabled?: boolean;

  // Controlled value
  value: TValue | null;
  onChange: (value: TValue | null, option: TOption | null) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;

  // Provide initial option if value exists but options aren't fetched yet
  initialOptionLabel?: string;
}

export function GenericAsyncAutocomplete<TOption, TValue>(
  props: GenericAsyncAutocompleteProps<TOption, TValue>
) {
  const {
    queryKeyPrefix,
    queryFn,
    minQueryLength = 1,
    debounceMs = 300,
    getOptionLabel,
    getOptionValue,
    isOptionEqualToValue,
    label,
    placeholder,
    noOptionsText,
    disabled = false,
    value,
    onChange,
    onBlur,
    error,
    helperText,
    initialOptionLabel,
  } = props;

  const [inputValue, setInputValue] = useState("");
  const [selectedOption, setSelectedOption] = useState<TOption | null>(null);

  // If value is provided from external state but we don't have the object yet,
  // we try to use initialOptionLabel to display something temporarily
  const [hasSetInitial, setHasSetInitial] = useState(false);

  useEffect(() => {
    if (value != null && !selectedOption && initialOptionLabel && !hasSetInitial) {
      setInputValue(initialOptionLabel);
      setHasSetInitial(true);
    }
  }, [value, selectedOption, initialOptionLabel, hasSetInitial]);

  const [query, setQuery] = useState("");
  const debouncedSetQuery = useMemo(
    () => debounce((val: string) => setQuery(val.trim()), debounceMs),
    [debounceMs]
  );

  useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

  const handleInputChange = useCallback(
    (_: React.SyntheticEvent, val: string, reason: AutocompleteInputChangeReason) => {
      setInputValue(val);
      if (reason === "input") {
        debouncedSetQuery(val);
        return;
      }
      debouncedSetQuery.cancel();
      if (reason === "clear") {
        setQuery("");
      }
    },
    [debouncedSetQuery]
  );

  const {
    data: options = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [queryKeyPrefix, "autocomplete", query],
    queryFn: () => queryFn(query),
    enabled: query.length >= minQueryLength,
    staleTime: 60 * 1000,
  });

  const loading = isLoading || isFetching;

  // Sync external form value into local state if it updates externally
  useEffect(() => {
    if (value == null) {
      if (selectedOption !== null) {
        setSelectedOption(null);
        setInputValue("");
      }
      return;
    }

    // Attempt to resolve value to option if options are present
    const matchedOption = options.find((opt) =>
      isOptionEqualToValue ? isOptionEqualToValue(opt, value) : getOptionValue(opt) === value
    );

    if (matchedOption) {
      const isSame =
        selectedOption && getOptionValue(selectedOption) === getOptionValue(matchedOption);

      // If selectedOption is currently null, setting it will trigger an Autocomplete "reset" event,
      // which will overwrite whatever the user is currently typing.
      // We should only populate selectedOption if the user hasn't started typing a custom search yet.
      const isUserActivelyTyping = inputValue !== "" && inputValue !== initialOptionLabel;

      if (!isSame) {
        if (selectedOption !== null || !isUserActivelyTyping) {
          setSelectedOption(matchedOption);
        }
      }
    } else if (selectedOption && getOptionValue(selectedOption) !== value) {
      // External value changed but it's not in the currently loaded options and not matching selectedOption
      setSelectedOption(null);
    }
  }, [
    value,
    options,
    getOptionValue,
    isOptionEqualToValue,
    selectedOption,
    inputValue,
    initialOptionLabel,
  ]);

  return (
    <Autocomplete
      fullWidth
      autoSelect={false}
      autoHighlight={false}
      options={options}
      getOptionLabel={(option) => {
        // Fallback for cases where string is passed directly (shouldn't happen with strict types, but good practice)
        if (typeof option === "string") return option;
        return getOptionLabel(option);
      }}
      value={selectedOption}
      inputValue={inputValue}
      disabled={disabled}
      onInputChange={handleInputChange}
      onChange={(_event, option) => {
        setSelectedOption(option);
        if (option != null) {
          onChange(getOptionValue(option), option);
        } else {
          onChange(null, null);
        }
      }}
      onBlur={onBlur}
      loading={loading}
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, v) =>
        isOptionEqualToValue
          ? isOptionEqualToValue(option, getOptionValue(v))
          : getOptionValue(option) === getOptionValue(v)
      }
      noOptionsText={
        noOptionsText
          ? noOptionsText(query)
          : query.length < minQueryLength
            ? "Tapez pour rechercher"
            : "Aucun résultat trouvé"
      }
      renderInput={(params) => {
        const { InputProps, ...restParams } = params;
        return (
          <TextField
            {...restParams}
            label={label}
            variant="outlined"
            error={error}
            helperText={helperText}
            placeholder={placeholder}
            InputProps={{
              ...InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} /> : null}
                  {InputProps.endAdornment}
                </>
              ),
            }}
          />
        );
      }}
    />
  );
}
